import { authClient } from "../../../auth/authClient";

const DEFAULT_TIMEOUT_MS = 30000;

const stripTrailingSlash = (value) => String(value || "").trim().replace(/\/+$/, "");

const getApiRoots = () => {
  const envUrl = stripTrailingSlash(process.env.REACT_APP_MORFEUSZ_4E_API_URL);

  if (envUrl) {
    const marker = "/wp-json/";
    const idx = envUrl.indexOf(marker);
    if (idx >= 0) {
      const wpJsonRoot = `${envUrl.slice(0, idx)}${marker}`;
      return {
        wp: `${wpJsonRoot}wp/v2`,
        morfeusz: `${wpJsonRoot}morfeusz/v1`,
      };
    }
    return {
      wp: `${envUrl}/wp-json/wp/v2`,
      morfeusz: `${envUrl}/wp-json/morfeusz/v1`,
    };
  }

  if (typeof window !== "undefined" && window.location?.origin) {
    return {
      wp: `${window.location.origin}/wp-json/wp/v2`,
      morfeusz: `${window.location.origin}/wp-json/morfeusz/v1`,
    };
  }

  return { wp: "", morfeusz: "" };
};

const withTimeout = async (promise, timeoutMs = DEFAULT_TIMEOUT_MS) => {
  let timeoutId;
  const timeout = new Promise((_, reject) => {
    timeoutId = window.setTimeout(() => reject(new Error("request_timeout")), timeoutMs);
  });

  try {
    return await Promise.race([promise, timeout]);
  } finally {
    window.clearTimeout(timeoutId);
  }
};

const decodeHtml = (value) => {
  const input = String(value || "");
  if (typeof document === "undefined") return input;
  const textarea = document.createElement("textarea");
  textarea.innerHTML = input;
  return textarea.value;
};

const stripHtml = (value) => String(value || "").replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();

const getEmbeddedImage = (post) => {
  const media = post?._embedded?.["wp:featuredmedia"]?.[0];
  const source = media?.media_details?.sizes?.large?.source_url
    || media?.media_details?.sizes?.medium_large?.source_url
    || media?.media_details?.sizes?.medium?.source_url
    || media?.source_url
    || "";
  return {
    src: source,
    alt: decodeHtml(media?.alt_text || post?.title?.rendered || ""),
  };
};

const normalizePost = (post) => {
  const image = getEmbeddedImage(post);
  return {
    id: post?.id,
    slug: post?.slug || "",
    title: decodeHtml(post?.title?.rendered || ""),
    excerpt: stripHtml(decodeHtml(post?.excerpt?.rendered || "")),
    content: post?.content?.rendered || "",
    date: post?.date || "",
    link: post?.link || "",
    commentStatus: post?.comment_status || "",
    image,
  };
};

const normalizeComment = (comment) => ({
  id: comment?.id,
  post: comment?.post,
  parent: comment?.parent || 0,
  authorName: decodeHtml(comment?.authorName || comment?.author_name || ""),
  date: comment?.date || "",
  contentText: comment?.contentText || stripHtml(decodeHtml(comment?.content?.rendered || "")),
});

const buildUrl = (rootName, path, params = {}) => {
  const roots = getApiRoots();
  const baseUrl = roots[rootName] || "";
  if (!baseUrl) return "";

  const url = new URL(`${baseUrl}${path}`);
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "") return;
    url.searchParams.set(key, String(value));
  });
  return url.toString();
};

const requestJson = async (rootName, path, params, options = {}) => {
  const url = buildUrl(rootName, path, params);
  if (!url) {
    return { ok: false, code: "not_configured", message: "Blog nie jest jeszcze skonfigurowany." };
  }

  try {
    const response = await withTimeout(fetch(url, {
      method: options.method || "GET",
      credentials: "include",
      headers: {
        ...(options.body ? { "Content-Type": "application/json" } : {}),
        ...(authClient.getToken?.() ? { Authorization: `Bearer ${authClient.getToken()}` } : {}),
      },
      ...(options.body ? { body: JSON.stringify(options.body) } : {}),
    }));
    const data = await response.json().catch(() => null);
    if (!response.ok || data?.ok === false) {
      return {
        ok: false,
        code: data?.code || "wp_rest_error",
        status: response.status,
        message: data?.message || "Nie udało się pobrać bloga.",
      };
    }

    return {
      ok: true,
      data,
      total: Number(response.headers.get("X-WP-Total") || 0),
      totalPages: Number(response.headers.get("X-WP-TotalPages") || 0),
    };
  } catch (error) {
    return {
      ok: false,
      code: error?.message === "request_timeout" ? "request_timeout" : "network_error",
      message: error?.message === "request_timeout"
        ? "Blog nie odpowiedział na czas. Spróbuj za chwilę."
        : "Nie udało się połączyć z blogiem. Spróbuj za chwilę.",
    };
  }
};

export const blogClient = Object.freeze({
  list: async ({ page = 1, search = "" } = {}) => {
    const result = await requestJson("wp", "/md_blog", {
      _embed: 1,
      per_page: 12,
      page,
      search: search.trim(),
      orderby: "date",
      order: "desc",
    });
    if (!result.ok) return result;
    return { ...result, posts: Array.isArray(result.data) ? result.data.map(normalizePost) : [] };
  },

  getBySlug: async (slug) => {
    const result = await requestJson("wp", "/md_blog", {
      _embed: 1,
      slug,
      per_page: 1,
    });
    if (!result.ok) return result;
    const post = Array.isArray(result.data) && result.data[0] ? normalizePost(result.data[0]) : null;
    return { ...result, post };
  },

  comments: async (postId) => {
    const result = await requestJson("morfeusz", "/blog/comments", { post: postId });
    if (!result.ok) return result;
    return { ...result, comments: Array.isArray(result.data?.comments) ? result.data.comments.map(normalizeComment) : [] };
  },

  addComment: async ({ postId, content }) => {
    const result = await requestJson("morfeusz", "/blog/comments", {}, {
      method: "POST",
      body: {
        post: postId,
        content,
      },
    });
    if (!result.ok) return result;
    return {
      ...result,
      comment: result.data?.comment ? normalizeComment(result.data.comment) : null,
      comments: Array.isArray(result.data?.comments) ? result.data.comments.map(normalizeComment) : [],
    };
  },
});
